/**
 * 1.5.0 shot detection, stream S2: Administration → Transcoding → "Shots and scrub images" (plan §1): detect at
 * ingest, sensitivity, minimum shot length, keyframe interval inside long shots, most scrub images per asset
 * (useShotSettings / useSaveShotSettings; admin). Mounted in TranscodingAdmin.tsx under the proxy format.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SHOT_DETECTION_DEFAULTS, SHOT_LIMITS, type ShotDetectionSettings } from '@prism/contracts';
import { useMe } from '../../../api/hooks';
import { useSaveShotSettings, useShotSettings } from '../../../api/hooks/shots';
import { errorMessage } from '../../../api/client';
import { toast } from '../../../components/Toasts';

/** Sensitivity in plain words (the slider's hint; the Detect shots dialog shows it too). */
export function sensitivityHint(v: number): string {
  if (v <= 25) return 'Low: only clear, hard cuts. Suits flashing lights and strobes.';
  if (v <= 60) return 'Medium: most hard cuts. Fades and dissolves may be missed.';
  if (v <= 85) return 'High: also softer cuts. Fast motion or flashes may add extra shots.';
  return 'Very high: almost every change of picture. Expect extra shots on motion and lighting changes.';
}

const clamp = (v: number, lim: { min: number; max: number }) => Math.min(lim.max, Math.max(lim.min, v));

export function ShotsCard() {
  const me = useMe().data;
  const isAdmin = Boolean(me?.permissions.includes('admin'));
  const q = useShotSettings();
  const save = useSaveShotSettings();
  const [d, setD] = useState<ShotDetectionSettings | null>(null);
  useEffect(() => {
    if (q.data && !d) {
      const { updatedAt: _u, ...rest } = q.data;
      void _u;
      setD(rest);
    }
  }, [q.data, d]);
  const s: ShotDetectionSettings = d ?? { ...SHOT_DETECTION_DEFAULTS };
  const saved = q.data;
  const dirty = Boolean(d && saved && (Object.keys(d) as Array<keyof ShotDetectionSettings>).some((k) => d[k] !== saved[k]));
  const disabled = !d || !isAdmin || save.isPending;
  const incomplete = [s.minShotSeconds, s.keyframeIntervalSeconds, s.maxTiles].some((v) => Number.isNaN(v));
  const shown = (v: number) => (Number.isNaN(v) ? '' : v);
  const set = (patch: Partial<ShotDetectionSettings>) => setD({ ...s, ...patch });
  const num = (key: 'minShotSeconds' | 'keyframeIntervalSeconds' | 'maxTiles', raw: string) => {
    // Empty while typing: NaN, and Save waits for a number.
    const n = raw.trim() === '' ? NaN : Number(raw);
    set({ [key]: key === 'maxTiles' && !Number.isNaN(n) ? Math.round(n) : n });
  };

  async function submit() {
    if (!d) return;
    const body: ShotDetectionSettings = {
      ...d,
      minShotSeconds: clamp(d.minShotSeconds, SHOT_LIMITS.minShotSeconds),
      keyframeIntervalSeconds: clamp(d.keyframeIntervalSeconds, SHOT_LIMITS.keyframeIntervalSeconds),
      maxTiles: Math.round(clamp(d.maxTiles, SHOT_LIMITS.maxTiles)),
    };
    try {
      const r = await save.mutateAsync(body);
      const { updatedAt: _u, ...rest } = r;
      void _u;
      setD(rest);
      toast('Shot settings saved');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <section className="panel-card shots-card" style={{ maxWidth: 980, marginTop: 16 }} data-testid="shots-card">
      <p className="section-label">Shots and scrub images</p>
      {q.isError && <p className="banner error">{errorMessage(q.error)}</p>}
      <label className="shots-switch">
        <input type="checkbox" checked={s.detectAtIngest} disabled={disabled} onChange={(e) => set({ detectAtIngest: e.target.checked })} data-testid="shots-at-ingest" />
        Detect shots at ingest
      </label>
      <span className="help">New video gets a read-only Shots layer and scrub images taken at each shot. Off: scrub images at a fixed interval, as before, and no Shots layer. Existing assets change only when someone runs Detect shots.</span>

      <div className="shots-grid">
        <div className="field shots-sensitivity">
          <label htmlFor="shots-sensitivity">Sensitivity <b className="mono" data-testid="shots-sensitivity-value">{s.sensitivity}</b></label>
          <input id="shots-sensitivity" type="range" min={SHOT_LIMITS.sensitivity.min} max={SHOT_LIMITS.sensitivity.max} step={1} value={s.sensitivity} disabled={disabled}
            onChange={(e) => set({ sensitivity: Number(e.target.value) })} data-testid="shots-sensitivity" />
          <span className="help" data-testid="shots-sensitivity-hint">{sensitivityHint(s.sensitivity)} Higher finds more cuts; lower it for flashing or fast-cut material.</span>
        </div>
        <div className="field">
          <label htmlFor="shots-min">Minimum shot length (s)</label>
          <input id="shots-min" className="input" type="number" min={SHOT_LIMITS.minShotSeconds.min} max={SHOT_LIMITS.minShotSeconds.max} step={0.1}
            value={shown(s.minShotSeconds)} disabled={disabled} onChange={(e) => num('minShotSeconds', e.target.value)} data-testid="shots-min" />
          <span className="help">Cuts closer than this to the one before are ignored, so flashes and strobes don’t make shots ({SHOT_LIMITS.minShotSeconds.min}–{SHOT_LIMITS.minShotSeconds.max} s).</span>
        </div>
        <div className="field">
          <label htmlFor="shots-interval">Keyframe interval inside long shots (s)</label>
          <input id="shots-interval" className="input" type="number" min={SHOT_LIMITS.keyframeIntervalSeconds.min} max={SHOT_LIMITS.keyframeIntervalSeconds.max} step={1}
            value={shown(s.keyframeIntervalSeconds)} disabled={disabled} onChange={(e) => num('keyframeIntervalSeconds', e.target.value)} data-testid="shots-interval" />
          <span className="help">A shot longer than this gets an extra scrub image every interval ({SHOT_LIMITS.keyframeIntervalSeconds.min}–{SHOT_LIMITS.keyframeIntervalSeconds.max} s).</span>
        </div>
        <div className="field">
          <label htmlFor="shots-max">Most scrub images per asset</label>
          <input id="shots-max" className="input" type="number" min={SHOT_LIMITS.maxTiles.min} max={SHOT_LIMITS.maxTiles.max} step={10}
            value={shown(s.maxTiles)} disabled={disabled} onChange={(e) => num('maxTiles', e.target.value)} data-testid="shots-max" />
          <span className="help">With more shots than this, an even selection gets scrub images; the Shots layer keeps every shot ({SHOT_LIMITS.maxTiles.min}–{SHOT_LIMITS.maxTiles.max}).</span>
        </div>
      </div>
      <p className="help shots-note" data-testid="shots-forced-note">The extra keyframes inside long shots add scrub images only, never shots: the Shots layer has only real cuts.</p>
      {isAdmin ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn primary sm" disabled={disabled || !dirty || incomplete} onClick={() => void submit()} data-testid="shots-save">
            {save.isPending && <Loader2 size={13} className="spin" />} Save
          </button>
          <button className="btn ghost sm" disabled={disabled} onClick={() => setD({ ...SHOT_DETECTION_DEFAULTS })}>Use the defaults</button>
          <span className="faint" style={{ fontSize: 12 }}>Applies to video ingested from now on and to Detect shots.</span>
        </div>
      ) : <p className="faint" style={{ fontSize: 12 }}>Only administrators change these settings.</p>}
    </section>
  );
}
